import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  advisors,
  brokers,
  FUNCIONALIDADE_ASSESSORIA,
  type AdvisorBroker,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCpfCnpj,
  formatRate,
  parseRate,
  rateInputError,
  rateToInput,
} from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';

import { AdvisorMembersSection } from './AdvisorMembersSection';
import {
  PENDENCIA_COMISSAO_ASSESSORIA,
  PENDENCIA_SESSAO_ASSESSOR,
  PERMISSAO_ASSESSORIA_EDITAR,
  PERMISSAO_COMISSAO,
} from './rotulos';

/** Comissao da assessoria, digitada em percentual e enviada em pontos-base. */
function CommissionField({
  valor,
  onChange,
  podeComissao,
}: {
  valor: string;
  onChange: (valor: string) => void;
  podeComissao: boolean;
}) {
  return (
    <Field
      label="Comissao da assessoria (%)"
      inputMode="decimal"
      value={valor}
      disabled={!podeComissao}
      onChange={(evento) => onChange(evento.target.value)}
      error={rateInputError(valor)}
      hint={
        podeComissao
          ? `${PENDENCIA_COMISSAO_ASSESSORIA} Vazio deixa sem comissao definida.`
          : 'A comissao pede a alcada da comissao (corretores.comissao.update).'
      }
    />
  );
}

function IncludeBrokerDialog({
  advisorId,
  podeComissao,
  onClose,
  onIncluida,
}: {
  advisorId: string;
  podeComissao: boolean;
  onClose: () => void;
  onIncluida: () => void;
}) {
  const toast = useToast();
  const [busca, setBusca] = useState('');
  const [corretora, setCorretora] = useState('');
  const [comissao, setComissao] = useState('');

  const opcoes = useQuery({
    queryKey: ['brokers', 'busca-assessoria', busca],
    queryFn: ({ signal }) => brokers.list({ q: busca, limit: 20 }, signal),
  });

  const incluir = useMutation({
    mutationFn: () =>
      advisors.addBroker(advisorId, corretora, comissao ? parseRate(comissao) : null),
    onSuccess: (vinculo) => {
      toast.success(`${vinculo.broker.legal_name} incluida na assessoria.`);
      onIncluida();
    },
  });

  const erroComissao = rateInputError(comissao);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!corretora || erroComissao) return;
    incluir.mutate();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Incluir corretora"
      description="A corretora fica em uma assessoria so. A inclusao fica no historico da corretora."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={incluir.isPending}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="incluir-corretora"
            loading={incluir.isPending}
            disabled={!corretora || erroComissao !== undefined}
          >
            Incluir
          </Button>
        </>
      }
    >
      <form id="incluir-corretora" onSubmit={enviar} className="flex flex-col gap-3">
        {incluir.error ? (
          <Alert tone="danger" title="Nao foi possivel incluir">
            {incluir.error.message}
          </Alert>
        ) : null}
        <Field
          label="Buscar corretora"
          placeholder="Razao social ou CNPJ"
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
        />
        <Select
          label="Corretora"
          placeholder="Selecione a corretora"
          options={(opcoes.data?.items ?? []).map((item) => ({
            value: item.id,
            label: `${item.legal_name} (${formatCpfCnpj(item.document)})`,
          }))}
          value={corretora}
          onChange={(evento) => setCorretora(evento.target.value)}
        />
        <CommissionField
          valor={comissao}
          onChange={setComissao}
          podeComissao={podeComissao}
        />
      </form>
    </Modal>
  );
}

function CommissionDialog({
  advisorId,
  vinculo,
  onClose,
  onSalva,
}: {
  advisorId: string;
  vinculo: AdvisorBroker;
  onClose: () => void;
  onSalva: () => void;
}) {
  const toast = useToast();
  const [comissao, setComissao] = useState(
    vinculo.commission_bps === null ? '' : rateToInput(vinculo.commission_bps),
  );
  const erro = rateInputError(comissao);
  const salvar = useMutation({
    mutationFn: () =>
      advisors.updateBroker(
        advisorId,
        vinculo.broker_id,
        comissao ? parseRate(comissao) : null,
      ),
    onSuccess: () => {
      toast.success('Comissao da assessoria salva.');
      onSalva();
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={`Comissao da assessoria: ${vinculo.broker.legal_name}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={salvar.isPending}>
            Cancelar
          </Button>
          <Button
            loading={salvar.isPending}
            disabled={erro !== undefined}
            onClick={() => salvar.mutate()}
          >
            Salvar comissao
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {salvar.error ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {salvar.error.message}
          </Alert>
        ) : null}
        <CommissionField valor={comissao} onChange={setComissao} podeComissao />
      </div>
    </Modal>
  );
}

/**
 * Corretoras da assessoria (/corretor-assessoria do OnPoint, que o ADMIN nao abria).
 * Lista as corretoras com a comissao da assessoria, e inclui, altera e retira. As
 * pessoas da assessoria, que entram pela tela da assessoria e atuam por uma das
 * corretoras, ficam na secao propria (P-ACE-2).
 */
export function AdvisorBrokersPage() {
  const { advisorId = '' } = useParams();
  const { can, hasFeature } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [incluindo, setIncluindo] = useState(false);
  const [alterando, setAlterando] = useState<AdvisorBroker | null>(null);
  const [retirando, setRetirando] = useState<AdvisorBroker | null>(null);

  const ligada = hasFeature(FUNCIONALIDADE_ASSESSORIA);
  const podeEditar = can(PERMISSAO_ASSESSORIA_EDITAR);
  const podeComissao = can(PERMISSAO_COMISSAO);

  const assessoria = useQuery({
    queryKey: ['advisor', advisorId],
    queryFn: ({ signal }) => advisors.get(advisorId, signal),
    enabled: ligada && advisorId !== '',
  });
  const corretoras = useQuery({
    queryKey: ['advisor', advisorId, 'brokers'],
    queryFn: ({ signal }) => advisors.brokers(advisorId, signal),
    enabled: ligada && advisorId !== '',
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['advisor', advisorId] });
    void queryClient.invalidateQueries({ queryKey: ['advisors'] });
    void queryClient.invalidateQueries({ queryKey: ['brokers'] });
  }

  const retirar = useMutation({
    mutationFn: (vinculo: AdvisorBroker) =>
      advisors.removeBroker(advisorId, vinculo.broker_id),
    onSuccess: () => {
      toast.success('Corretora retirada da assessoria.');
      setRetirando(null);
      invalidar();
    },
    onError: (erro: Error) => {
      setRetirando(null);
      toast.error(erro.message);
    },
  });

  if (!ligada) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Corretoras da assessoria" />
        <EmptyState
          icon="users"
          title="Assessoria desligada nesta empresa"
          description="A administracao da plataforma liga a assessoria quando a empresa trabalhar com ela."
        />
      </div>
    );
  }

  const colunas: Column<AdvisorBroker>[] = [
    {
      key: 'corretora',
      header: 'Corretora',
      render: (vinculo) => (
        <Link
          to={`/corretores/${vinculo.broker_id}`}
          className="font-medium text-brand-700 hover:underline"
        >
          {vinculo.broker.legal_name}
        </Link>
      ),
    },
    {
      key: 'cnpj',
      header: 'CNPJ',
      render: (vinculo) => (
        <span className="whitespace-nowrap tabular-nums">
          {formatCpfCnpj(vinculo.broker.document)}
        </span>
      ),
    },
    {
      key: 'etapa',
      header: 'Etapa',
      hideBelow: 'sm',
      render: (vinculo) => <StatusBadge kind="corretor" value={vinculo.broker.stage} />,
    },
    {
      key: 'comissao-corretor',
      header: 'Comissao do corretor',
      align: 'right',
      hideBelow: 'md',
      render: (vinculo) => formatRate(vinculo.broker.commission_bps),
    },
    {
      key: 'comissao-assessoria',
      header: 'Comissao da assessoria',
      align: 'right',
      render: (vinculo) =>
        vinculo.commission_bps === null ? (
          <span className="text-ink-400">nao definida</span>
        ) : (
          formatRate(vinculo.commission_bps)
        ),
    },
    {
      key: 'digital',
      header: 'Digital',
      hideBelow: 'md',
      render: (vinculo) =>
        vinculo.broker.is_digital ? <Badge tone="info">Digital</Badge> : 'Nao',
    },
    {
      key: 'acoes',
      header: 'Acoes',
      render: (vinculo) => (
        <div className="flex gap-1">
          {podeEditar && podeComissao ? (
            <AcaoComIcone
              acao="editar"
              icone="percent"
              rotulo="Alterar comissao"
              onClick={() => setAlterando(vinculo)}
            />
          ) : null}
          {podeEditar ? (
            <AcaoComIcone
              acao="retirar"
              rotulo="Retirar"
              onClick={() => setRetirando(vinculo)}
            />
          ) : null}
          <AcaoComIcone
            acao="acessarComo"
            rotulo="Acessar como corretora"
            disabled
            motivo={PENDENCIA_SESSAO_ASSESSOR}
          />
        </div>
      ),
    },
  ];

  const nome = assessoria.data?.name ?? 'Assessoria';

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={`Corretoras da assessoria ${nome}`}
        description={PENDENCIA_COMISSAO_ASSESSORIA}
        actions={
          <>
            <Button variant="ghost" onClick={() => void navigate('/assessorias')}>
              Lista de assessorias
            </Button>
            {podeEditar && assessoria.data?.active ? (
              <Button onClick={() => setIncluindo(true)}>Incluir corretora</Button>
            ) : null}
          </>
        }
      />

      {assessoria.isError || corretoras.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a assessoria">
          {(assessoria.error ?? corretoras.error)?.message}
        </Alert>
      ) : null}
      {assessoria.data && !assessoria.data.active ? (
        <Alert tone="warning" title="Assessoria inativa">
          A assessoria inativa continua com as corretoras dela e nao recebe corretora
          nova.
        </Alert>
      ) : null}
      <Alert tone="info" title="Acesso do assessor">
        {PENDENCIA_SESSAO_ASSESSOR}
      </Alert>

      <AdvisorMembersSection
        advisorId={advisorId}
        podeEditar={podeEditar}
        ativa={assessoria.data?.active ?? false}
      />

      <Card>
        <DataTable
          columns={colunas}
          items={corretoras.data ?? []}
          keyOf={(vinculo) => vinculo.broker_id}
          loading={corretoras.isPending}
          empty={
            <EmptyState
              icon="users"
              title="Nenhuma corretora nesta assessoria"
              description="Inclua as corretoras que a assessoria reune."
            />
          }
        />
      </Card>

      {incluindo ? (
        <IncludeBrokerDialog
          advisorId={advisorId}
          podeComissao={podeComissao}
          onClose={() => setIncluindo(false)}
          onIncluida={() => {
            setIncluindo(false);
            invalidar();
          }}
        />
      ) : null}
      {alterando ? (
        <CommissionDialog
          advisorId={advisorId}
          vinculo={alterando}
          onClose={() => setAlterando(null)}
          onSalva={() => {
            setAlterando(null);
            invalidar();
          }}
        />
      ) : null}
      <ConfirmDialog
        open={retirando !== null}
        onClose={() => setRetirando(null)}
        onConfirm={() => {
          if (retirando) retirar.mutate(retirando);
        }}
        title={`Retirar ${retirando?.broker.legal_name ?? ''}`}
        message="A corretora sai da assessoria, e a comissao da assessoria sobre ela deixa de existir. A retirada fica no historico da corretora."
        confirmLabel="Retirar"
        tone="danger"
        loading={retirar.isPending}
      />
    </div>
  );
}
